/**
 * Web API client for the vault backend. Same auth model as the extension
 * (magic-link → access + refresh JWT, single-flight refresh on 401) but tokens
 * live in localStorage and requests are same-origin (/v1/* — Vite proxies in
 * dev, nginx in prod). The web app is a READER/manager of the vault: it never
 * sweeps tabs (that's the extension), it browses, searches, dedups, organizes.
 */
import type {
  Content,
  DupGroup,
  Item,
  SearchHit,
  Sweep,
} from '@vault/shared';
import type { Collection, ShareLink } from '@vault/shared';

/** GET /collections/:id/items response (API-side shape, not in shared). */
export interface CollectionItemsPage {
  collection: Collection;
  items: Item[];
  nextBefore: number | null;
}

const TOKENS_KEY = 'tv.tokens';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  email: string;
}

export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}
export function saveTokens(t: Tokens): void {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(t));
}
export function clearTokens(): void {
  localStorage.removeItem(TOKENS_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

/** Emitted when the session becomes unauthenticated so the app can redirect. */
type AuthListener = () => void;
const authListeners = new Set<AuthListener>();
export function onAuthLost(fn: AuthListener): () => void {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

const BASE = '/v1';
let refreshInFlight: Promise<boolean> | null = null;

/** Cross-tab "the vault changed" signal. A BroadcastChannel never delivers to
 *  the tab that posted, so a mutation here notifies OTHER open tabs, which show
 *  the ConflictBanner ("reload to see the latest"). No-op where unsupported. */
const vaultChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tv-vault') : null;
function announceVaultChange(): void {
  try {
    vaultChannel?.postMessage('changed');
  } catch {
    /* channel closed */
  }
}

async function doRefresh(): Promise<boolean> {
  const t = loadTokens();
  if (!t) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: t.refreshToken, deviceId: t.deviceId }),
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        clearTokens();
        authListeners.forEach((fn) => fn());
      }
      return false;
    }
    const data = (await res.json()) as { accessToken: string; refreshToken: string };
    saveTokens({ ...t, accessToken: data.accessToken, refreshToken: data.refreshToken });
    return true;
  } catch {
    return false;
  }
}

function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function request<T>(path: string, init: RequestInit = {}, allowRefresh = true): Promise<T> {
  const t = loadTokens();
  const headers: Record<string, string> = {};
  if (init.body && !(init.headers && 'Content-Type' in (init.headers as object))) {
    headers['Content-Type'] = 'application/json';
  }
  if (t?.accessToken) headers.Authorization = `Bearer ${t.accessToken}`;
  Object.assign(headers, (init.headers as Record<string, string>) ?? {});

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && allowRefresh && t?.accessToken) {
    if (await refreshOnce()) return request<T>(path, init, false);
    clearTokens();
    authListeners.forEach((fn) => fn());
  }
  if (!res.ok) {
    let body: { error?: string; message?: string } = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, body.error ?? 'request_failed', body.message);
  }
  // A successful mutation may have changed data other tabs are showing.
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET') announceVaultChange();

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── auth ───────────────────────────────────────────────────────────────────
export const auth = {
  requestLink: (email: string) =>
    request<void>('/auth/request-link', { method: 'POST', body: JSON.stringify({ email }) }),
  verify: (token: string) =>
    request<{ accessToken: string; refreshToken: string; deviceId: string; user: { id: string; email: string; plan: 'free' | 'pro' } }>(
      '/auth/verify',
      { method: 'POST', body: JSON.stringify({ token, device: { name: 'Web app', browser: 'chrome', platform: 'web' } }) },
    ),
  companion: (device: { name: string; browser: string; platform: string }) =>
    request<{ accessToken: string; refreshToken: string; deviceId: string; user: { id: string; email: string; plan: 'free' | 'pro' } }>(
      '/auth/companion',
      { method: 'POST', body: JSON.stringify({ device }) },
    ),
  logout: () => request<void>('/auth/logout', { method: 'POST', body: JSON.stringify({}) }),
};

// ── account ──────────────────────────────────────────────────────────────
export interface Usage {
  plan: 'free' | 'pro';
  limits: Record<string, unknown>;
  counts: { sweeps: number; items: number; contents: number; devices: number };
}
export const account = {
  usage: () => request<Usage>('/account/usage'),
  exportUrl: () => `${BASE}/account/export`,
  deleteAccount: () => request<void>('/account', { method: 'DELETE' }),
};

// ── vault (timeline / detail / content) ────────────────────────────────────
export const vault = {
  sweeps: (opts: { limit?: number; before?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.before) p.set('before', String(opts.before));
    return request<{ sweeps: Sweep[]; nextBefore: number | null }>(`/vault/sweeps?${p}`);
  },
  sweep: (id: string) => request<{ sweep: Sweep; items: Item[] }>(`/vault/sweeps/${id}`),
  content: (itemId: string) => request<{ content: Content }>(`/vault/content/${itemId}`),
  deleteItem: (id: string) => request<void>(`/vault/items/${id}`, { method: 'DELETE' }),
  deleteSweep: (id: string) => request<void>(`/vault/sweeps/${id}`, { method: 'DELETE' }),
};

// ── trash (soft-deleted, 14-day recovery) ──────────────────────────────────
export interface TrashEntry {
  kind: 'sweep' | 'item';
  id: string;
  title: string | null;
  url: string | null;
  browser: string | null;
  deletedAt: number;
  /** deletedAt + 14 days — when the retention reaper purges it for good. */
  purgeAt: number;
  /** sweeps only: how many tabs go down with it. */
  tabCount: number | null;
}
export const trash = {
  list: () => request<{ entries: TrashEntry[] }>('/vault/trash'),
  restore: (kind: 'sweep' | 'item', id: string) =>
    request<void>('/vault/trash/restore', { method: 'POST', body: JSON.stringify({ kind, id }) }),
  purge: (kind: 'sweep' | 'item', id: string) =>
    request<void>('/vault/trash', { method: 'DELETE', body: JSON.stringify({ kind, id }) }),
  empty: () => request<void>('/vault/trash/all', { method: 'DELETE' }),
};

// ── version history (Pro) ──────────────────────────────────────────────────
export interface ContentVersion {
  id: string;
  capturedAt: number;
  wordCount: number;
  excerpt: string;
  /** true for the version currently shown in Page Detail. */
  current: boolean;
}
export const versions = {
  list: (itemId: string) => request<{ versions: ContentVersion[] }>(`/vault/content/${itemId}/versions`),
  get: (itemId: string, versionId: string) =>
    request<{ content: Content }>(`/vault/content/${itemId}/versions/${versionId}`),
};

// ── search ─────────────────────────────────────────────────────────────────
export interface SearchParams {
  mode?: 'hybrid' | 'text' | 'semantic';
  limit?: number;
  browser?: string;
  from?: number;
  to?: number;
}
export const search = (q: string, opts: SearchParams = {}) => {
  const p = new URLSearchParams({ q });
  if (opts.mode) p.set('mode', opts.mode);
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.browser) p.set('browser', opts.browser);
  if (opts.from) p.set('from', String(opts.from));
  if (opts.to) p.set('to', String(opts.to));
  return request<{ hits: SearchHit[] }>(`/search?${p}`);
};

// ── dedup ────────────────────────────────────────────────────────────────
export const dedup = {
  live: () => request<{ groups: DupGroup[] }>('/dedup/live'),
  close: (commands: Array<{ deviceId: string; tabKeys: string[] }>) =>
    request<void>('/dedup/close', { method: 'POST', body: JSON.stringify({ commands }) }),
};

// ── collections ──────────────────────────────────────────────────────────
export const collections = {
  list: () => request<{ collections: Collection[] }>('/collections'),
  create: (name: string, color?: string | null) =>
    request<{ collection: Collection }>('/collections', {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    }),
  rename: (id: string, patch: { name?: string; color?: string | null }) =>
    request<{ collection: Collection }>(`/collections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remove: (id: string) => request<void>(`/collections/${id}`, { method: 'DELETE' }),
  items: (id: string, opts: { limit?: number; before?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.before) p.set('before', String(opts.before));
    return request<CollectionItemsPage>(`/collections/${id}/items?${p}`);
  },
  addItems: (id: string, itemIds: string[]) =>
    request<{ added: number }>(`/collections/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ itemIds }),
    }),
  removeItems: (id: string, itemIds: string[]) =>
    request<void>(`/collections/${id}/items`, {
      method: 'DELETE',
      body: JSON.stringify({ itemIds }),
    }),
};

// ── import ────────────────────────────────────────────────────────────────
export const importVault = {
  onetab: (text: string) =>
    request<{ sweepId: string; imported: number; skippedDuplicates: number }>('/import/onetab', {
      method: 'POST',
      body: text,
      headers: { 'Content-Type': 'text/plain' },
    }),
  bookmarks: (html: string) =>
    request<{ sweepId: string; imported: number; skippedDuplicates: number }>('/import/bookmarks', {
      method: 'POST',
      body: html,
      headers: { 'Content-Type': 'text/html' },
    }),
  sessionBuddy: (json: string) =>
    request<{ sweepId: string; imported: number; skippedDuplicates: number }>('/import/session-buddy', {
      method: 'POST',
      body: json,
      headers: { 'Content-Type': 'application/json' },
    }),
};

// ── sharing ──────────────────────────────────────────────────────────────
export const share = {
  create: (kind: 'collection' | 'sweep', targetId: string, expiresInDays?: number) =>
    request<{ share: ShareLink }>('/share', {
      method: 'POST',
      body: JSON.stringify({ kind, targetId, expiresInDays }),
    }),
  revoke: (token: string) => request<void>(`/share/${token}`, { method: 'DELETE' }),
};

// ── billing ──────────────────────────────────────────────────────────────
export type SubscriptionStatus = 'active' | 'past_due' | 'cancelled' | 'none';
export interface Subscription {
  status: SubscriptionStatus;
  interval: 'monthly' | 'yearly' | null;
  /** epoch ms of next renewal, or grace-period end when past_due. */
  renewsAt: number | null;
  cardBrand: string | null;
  cardLast4: string | null;
  /** Lemon Squeezy customer portal, for updating card / cancelling. */
  portalUrl: string | null;
}
export const billing = {
  plans: () => request<{ plans: Record<string, unknown>; pricing: { pro: { monthlyUsd: number; yearlyUsd: number } } }>('/billing/plans'),
  checkout: (interval: 'monthly' | 'yearly') =>
    request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ interval }),
    }),
  subscription: () => request<Subscription>('/billing/subscription'),
  portal: () => request<{ url: string }>('/billing/portal', { method: 'POST', body: JSON.stringify({}) }),
};

export const api = {
  auth,
  account,
  vault,
  trash,
  versions,
  search,
  dedup,
  collections,
  import: importVault,
  share,
  billing,
};
